import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { X, Filter, Share2, CheckCircle2, Save, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { VEHICLE_PLACEHOLDER_URL } from '../../constants/vehiclePlaceholder';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import CarCard from '../../components/ALLJsx/CarCard';
import CarComparison from '../../components/ALLJsx/CarComparison';
import FiltersSidebar, { MAKES_MODELS, BODY_TYPES } from '../../components/ALLJsx/FilterSidebar';
import { api } from '../../services/api';
import { getRecentlyViewedCars } from '../../services/recentlyViewedService';
import { applyRecommendations, getInventoryAgeBoost } from '../../utils/recommendationEngine';
import { useEnsureCars } from '../../hooks/useEnsureCars';
import { computeMarketComparison } from '../../utils/marketComparison';
import '../AllCss/Inventory.css';

const normalizeVinQuery = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const getCarFeatureList = (features) => {
  if (!features) return [];
  if (Array.isArray(features)) return features.filter(Boolean);
  if (typeof features === 'object') return Object.values(features).flat().filter(Boolean);
  return [];
};

const isLikelyVinQuery = (value) => {
  const v = normalizeVinQuery(value);
  // Allow partial VIN lookups (8+) and full VIN (17), excluding ambiguous I/O/Q.
  return (
    v.length >= 8 &&
    v.length <= 17 &&
    /^[A-HJ-NPR-Z0-9]+$/.test(v)
  );
};

function Inventory() {
  useEnsureCars();
  const { t } = useLocale();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const cars = useSelector((state) => state.cars.items);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [comparedCars, setComparedCars] = useState(() => {
    try {
      const saved = localStorage.getItem('comparedCars');
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.error('Error loading compared cars from localStorage:', error);
    }
    return [];
  });
  const [showComparison, setShowComparison] = useState(false);
  const maxCompare = 6;
  const [linkCopied, setLinkCopied] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [lastSuggestedName, setLastSuggestedName] = useState(''); // Track last suggested name
  const [email, setEmail] = useState('');
  const [emailNotificationType, setEmailNotificationType] = useState('instant'); // 'instant' or 'daily'
  const [saving, setSaving] = useState(false);

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const wishlistIds = useSelector((state) => state.wishlist.items);
  const [recentlyViewedCars, setRecentlyViewedCars] = useState([]);

  // Fetch recently viewed cars for recommendations
  useEffect(() => {
    const fetchRecentlyViewed = async () => {
      try {
        if (isAuthenticated && user?.id) {
          // For logged-in users, fetch from API
          const response = await api.get('/recently-viewed', {
            params: { limit: 20 },
          });
          if (response.data.success && response.data.data?.recentlyViewed) {
            // API returns { success: true, data: { recentlyViewed: [...] } }
            setRecentlyViewedCars(response.data.data.recentlyViewed);
          }
        } else {
          // For guests, use localStorage
          const localRecentlyViewed = getRecentlyViewedCars();
          // Map to include viewedAt timestamp from the item
          setRecentlyViewedCars(localRecentlyViewed.map(item => ({
            ...item.car,
            viewedAt: item.viewedAt || new Date().toISOString(),
          })));
        }
      } catch (error) {
        console.error('Error fetching recently viewed cars:', error);
        // Fallback to localStorage for guests
        if (!isAuthenticated) {
          const localRecentlyViewed = getRecentlyViewedCars();
          // Map to include viewedAt timestamp from the item
          setRecentlyViewedCars(localRecentlyViewed.map(item => ({
            ...item.car,
            viewedAt: item.viewedAt || new Date().toISOString(),
          })));
        }
      }
    };

    fetchRecentlyViewed();
  }, [isAuthenticated, user]);

  // Initialize email and suggested name when dialog opens
  useEffect(() => {
    if (showSaveDialog && isAuthenticated && user?.email) {
      setEmail(user.email);
    }
  }, [showSaveDialog, isAuthenticated, user]);

  // Debug logging (main site shows cars returned by GET /api/cars – backend filters by customer-visible statuses)
  useEffect(() => {
    if (cars && cars.length > 0) {
      const statusCounts = cars.reduce((acc, car) => {
        acc[car.status || 'no-status'] = (acc[car.status || 'no-status'] || 0) + 1;
        return acc;
      }, {});
    } else if (cars && cars.length === 0) {
    } else {
      console.warn('⚠️ No cars in Redux – check backend is running and GET /api/cars is accessible');
    }
  }, [cars]);

  const createEmptyFilters = () => ({
    sortBy: 'best',
    search: '',
    vin: '',
    priceMin: '',
    priceMax: '',
    make: '',
    selectedMakes: [],
    model: '',
    selectedModels: [],
    yearMin: '',
    yearMax: '',
    mileageMin: '',
    mileageMax: '',
    fuelType: '',
    selectedFuels: [],
    selectedSeats: [],
    selectedBodyTypes: [],
    selectedExteriorColors: [],
    selectedInteriorColors: [],
    selectedDrivetrains: [],
    selectedFeatures: [],
  });

  const [filters, setFilters] = useState({
    ...createEmptyFilters(),
    search: searchParams.get('search') || '',
    vin: searchParams.get('vin') || '',
  });
  const [filterPresets, setFilterPresets] = useState(() => {
    try {
      const raw = localStorage.getItem('inventoryFilterPresets');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  // Initialize filters from URL parameters
  useEffect(() => {
    const q = searchParams.get('search') || '';
    const vin = searchParams.get('vin') || '';
    const priceMin = searchParams.get('priceMin') || '';
    const priceMax = searchParams.get('priceMax') || '';
    const make = searchParams.get('make') || '';
    const bodyType = searchParams.get('bodyType') || '';
    const fuelType = searchParams.get('fuelType') || '';
    const yearMin = searchParams.get('yearMin') || '';
    const yearMax = searchParams.get('yearMax') || '';
    const mileageMin = searchParams.get('mileageMin') || '';
    const mileageMax = searchParams.get('mileageMax') || '';

    // Parse array parameters
    const makes = searchParams.get('makes') ? searchParams.get('makes').split(',') : [];
    const models = searchParams.get('models') ? searchParams.get('models').split(',') : [];
    const fuels = searchParams.get('fuels') ? searchParams.get('fuels').split(',') : [];
    const bodyTypes = searchParams.get('bodyTypes') ? searchParams.get('bodyTypes').split(',') : [];
    const seats = searchParams.get('seats') ? searchParams.get('seats').split(',') : [];
    const exteriorColors = searchParams.get('exteriorColors') ? searchParams.get('exteriorColors').split(',') : [];
    const interiorColors = searchParams.get('interiorColors') ? searchParams.get('interiorColors').split(',') : [];
    const drivetrains = searchParams.get('drivetrains') ? searchParams.get('drivetrains').split(',') : [];

    // Only update if there are URL parameters to apply
    if (q || vin || priceMin || priceMax || make || bodyType || fuelType || yearMin || yearMax || mileageMin || mileageMax ||
      makes.length > 0 || models.length > 0 || fuels.length > 0 || bodyTypes.length > 0 || seats.length > 0 ||
      exteriorColors.length > 0 || interiorColors.length > 0 || drivetrains.length > 0) {
      setFilters((prev) => ({
        ...prev,
        search: q || prev.search,
        vin: vin || prev.vin,
        priceMin: priceMin || prev.priceMin,
        priceMax: priceMax || prev.priceMax,
        yearMin: yearMin || prev.yearMin,
        yearMax: yearMax || prev.yearMax,
        mileageMin: mileageMin || prev.mileageMin,
        mileageMax: mileageMax || prev.mileageMax,
        selectedMakes: makes.length > 0 ? makes : (make ? [make] : prev.selectedMakes),
        selectedModels: models.length > 0 ? models : prev.selectedModels,
        selectedFuels: fuels.length > 0 ? fuels : prev.selectedFuels,
        selectedBodyTypes: bodyTypes.length > 0 ? bodyTypes.map(b => b.toLowerCase()) : (bodyType ? [bodyType.toLowerCase()] : prev.selectedBodyTypes),
        selectedSeats: seats.length > 0 ? seats : prev.selectedSeats,
        selectedExteriorColors: exteriorColors.length > 0 ? exteriorColors.map(c => c.toLowerCase()) : prev.selectedExteriorColors,
        selectedInteriorColors: interiorColors.length > 0 ? interiorColors.map(c => c.toLowerCase()) : prev.selectedInteriorColors,
        selectedDrivetrains: drivetrains.length > 0 ? drivetrains.map(d => d.toLowerCase()) : prev.selectedDrivetrains,
        fuelType: fuelType ? fuelType.toLowerCase() : prev.fuelType,
      }));
    }
  }, [searchParams]);

  function updateFilters(partial) {
    setFilters((prev) => ({ ...prev, ...partial }));
  }

  // Auto-convert search to specific filters when it matches known patterns
  useEffect(() => {
    if (!filters.search || typeof filters.search !== 'string') return;

    const searchLower = filters.search.toLowerCase().trim();
    if (!searchLower) return;

    // VIN quick lookup: when search looks like a VIN, move it to dedicated VIN filter.
    if (!filters.vin && isLikelyVinQuery(searchLower)) {
      setFilters(prev => ({
        ...prev,
        vin: normalizeVinQuery(searchLower),
        search: '',
      }));
      return;
    }

    // Check if search matches a seats pattern (e.g., "5 seats", "5 seat", "5")
    const seatsMatch = searchLower.match(/(\d+)\s*seats?/i);
    if (seatsMatch) {
      const seatNumber = String(parseInt(seatsMatch[1], 10));
      if (!filters.selectedSeats.includes(seatNumber)) {
        setFilters(prev => ({
          ...prev,
          selectedSeats: [...prev.selectedSeats, seatNumber],
          search: '' // Clear search after converting to filter
        }));
        return;
      }
    }

    // Check if search matches a body type
    const matchedBodyType = BODY_TYPES && Array.isArray(BODY_TYPES) ? BODY_TYPES.find(bt => {
      if (!bt || !bt.key || !bt.label) return false;
      return (
        bt.key.toLowerCase() === searchLower ||
        bt.label.toLowerCase() === searchLower ||
        (Array.isArray(bt.aliases) && bt.aliases.some(a => a && typeof a === 'string' && a.toLowerCase() === searchLower))
      );
    }) : null;
    if (matchedBodyType && matchedBodyType.key && !filters.selectedBodyTypes.includes(matchedBodyType.key)) {
      setFilters(prev => ({
        ...prev,
        selectedBodyTypes: [...prev.selectedBodyTypes, matchedBodyType.key],
        search: '' // Clear search after converting to filter
      }));
      return;
    }

    // Check if search matches a make
    const matchedMake = MAKES_MODELS && Array.isArray(MAKES_MODELS) ? MAKES_MODELS.find(m => {
      return m && m.make && typeof m.make === 'string' && m.make.toLowerCase() === searchLower;
    }) : null;
    if (matchedMake && matchedMake.make && !filters.selectedMakes.includes(matchedMake.make)) {
      setFilters(prev => ({
        ...prev,
        selectedMakes: [...prev.selectedMakes, matchedMake.make],
        search: '' // Clear search after converting to filter
      }));
      return;
    }

    // Check if search matches a fuel type
    let fuelKey = null;
    if (searchLower === 'gas' || searchLower === 'gasoline') {
      fuelKey = 'gasoline';
    } else if (['diesel', 'electric', 'hybrid', 'plug-in hybrid'].includes(searchLower)) {
      fuelKey = searchLower;
    }
    if (fuelKey && !filters.selectedFuels.includes(fuelKey)) {
      setFilters(prev => ({
        ...prev,
        selectedFuels: [...prev.selectedFuels, fuelKey],
        search: '' // Clear search after converting to filter
      }));
      return;
    }
  }, [filters.search, filters.vin, filters.selectedSeats, filters.selectedBodyTypes, filters.selectedMakes, filters.selectedFuels]);

  // Sync all filters to URL parameters
  useEffect(() => {
    const next = new URLSearchParams();

    // Add filters to URL
    if (filters.search?.trim()) next.set('search', filters.search.trim());
    if (filters.vin?.trim()) next.set('vin', normalizeVinQuery(filters.vin));
    if (filters.priceMin) next.set('priceMin', filters.priceMin);
    if (filters.priceMax) next.set('priceMax', filters.priceMax);
    if (filters.yearMin) next.set('yearMin', filters.yearMin);
    if (filters.yearMax) next.set('yearMax', filters.yearMax);
    if (filters.mileageMin) next.set('mileageMin', filters.mileageMin);
    if (filters.mileageMax) next.set('mileageMax', filters.mileageMax);

    // Array parameters
    if (filters.selectedMakes.length > 0) next.set('makes', filters.selectedMakes.join(','));
    if (filters.selectedModels.length > 0) next.set('models', filters.selectedModels.join(','));
    if (filters.selectedFuels.length > 0) next.set('fuels', filters.selectedFuels.join(','));
    if (filters.selectedBodyTypes.length > 0) next.set('bodyTypes', filters.selectedBodyTypes.join(','));
    if (filters.selectedSeats.length > 0) next.set('seats', filters.selectedSeats.join(','));
    if (filters.selectedExteriorColors.length > 0) next.set('exteriorColors', filters.selectedExteriorColors.join(','));
    if (filters.selectedInteriorColors.length > 0) next.set('interiorColors', filters.selectedInteriorColors.join(','));
    if (filters.selectedDrivetrains.length > 0) next.set('drivetrains', filters.selectedDrivetrains.join(','));

    // Update URL (use replace to avoid cluttering history)
    const newUrl = next.toString();
    const currentUrl = searchParams.toString();

    if (newUrl !== currentUrl) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);


  // Generate suggested name for saved search based on filters
  const getSuggestedSearchName = (currentFilters = filters) => {
    const parts = [];

    // Add makes (most important)
    if (currentFilters.selectedMakes && currentFilters.selectedMakes.length > 0) {
      if (currentFilters.selectedMakes.length === 1) {
        parts.push(currentFilters.selectedMakes[0]);
      } else if (currentFilters.selectedMakes.length === 2) {
        parts.push(currentFilters.selectedMakes.join(' & '));
      } else if (currentFilters.selectedMakes.length <= 3) {
        parts.push(currentFilters.selectedMakes.join(', '));
      } else {
        parts.push(currentFilters.selectedMakes.slice(0, 2).join(', ') + ' & more');
      }
    }

    // Add price range
    if (currentFilters.priceMax) {
      parts.push(`Under $${Number(currentFilters.priceMax).toLocaleString()}`);
    } else if (currentFilters.priceMin) {
      parts.push(`Over $${Number(currentFilters.priceMin).toLocaleString()}`);
    } else if (currentFilters.priceMin && currentFilters.priceMax) {
      parts.push(`$${Number(currentFilters.priceMin).toLocaleString()}-$${Number(currentFilters.priceMax).toLocaleString()}`);
    }

    // Add body type
    if (currentFilters.selectedBodyTypes && currentFilters.selectedBodyTypes.length > 0) {
      if (currentFilters.selectedBodyTypes.length === 1) {
        parts.push(currentFilters.selectedBodyTypes[0].charAt(0).toUpperCase() + currentFilters.selectedBodyTypes[0].slice(1));
      } else {
        parts.push(currentFilters.selectedBodyTypes.map(bt => bt.charAt(0).toUpperCase() + bt.slice(1)).join('/'));
      }
    }

    // Add year range
    if (currentFilters.yearMin || currentFilters.yearMax) {
      if (currentFilters.yearMin && currentFilters.yearMax) {
        parts.push(`${currentFilters.yearMin}-${currentFilters.yearMax}`);
      } else if (currentFilters.yearMin) {
        parts.push(`${currentFilters.yearMin}+`);
      } else if (currentFilters.yearMax) {
        parts.push(`Up to ${currentFilters.yearMax}`);
      }
    }

    // Add fuel type if specific
    if (currentFilters.selectedFuels && currentFilters.selectedFuels.length === 1) {
      const fuel = currentFilters.selectedFuels[0];
      if (fuel === 'electric') parts.push('Electric');
      else if (fuel === 'hybrid') parts.push('Hybrid');
      else if (fuel === 'plug-in hybrid') parts.push('PHEV');
    }

    // If no filters, return default
    if (parts.length === 0) {
      return 'My Saved Search';
    }

    return parts.join(' ');
  };

  // Initialize email and suggested name when dialog opens
  useEffect(() => {
    if (showSaveDialog) {
      // Set email
      if (isAuthenticated && user?.email) {
        setEmail(user.email);
      } else if (!isAuthenticated) {
        setEmail('');
      }

      // Set suggested search name based on current filters
      const suggestedName = getSuggestedSearchName(filters);
      if (suggestedName) {
        // Only update if name is empty or matches the last suggestion (user hasn't customized it)
        if (!searchName.trim() || searchName === lastSuggestedName) {
          setSearchName(suggestedName);
          setLastSuggestedName(suggestedName);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSaveDialog, isAuthenticated, user, filters]);

  // Share filtered URL
  const handleShareLink = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // Save search
  const handleSaveSearch = async () => {
    if (!searchName.trim()) {
      toast.error('Please enter a name for your saved search');
      return;
    }

    // Email validation for all users
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    // For non-authenticated users, prompt to create account
    if (!isAuthenticated) {
      const createAccount = await confirmDialog({
        title: 'Account required',
        description: 'To save your search, please create an account. Would you like to sign up now?',
        confirmLabel: 'Sign up',
        cancelLabel: 'Cancel',
      });
      if (createAccount) {
        setShowSaveDialog(false);
        window.location.href = '/auth?mode=register';
        return;
      }
      return;
    }

    try {
      setSaving(true);
      const searchData = {
        name: searchName.trim(),
        search: filters.search || null,
        priceMin: filters.priceMin || null,
        priceMax: filters.priceMax || null,
        make: filters.make || null,
        selectedMakes: filters.selectedMakes || [],
        model: filters.model || null,
        selectedModels: filters.selectedModels || [],
        yearMin: filters.yearMin || null,
        yearMax: filters.yearMax || null,
        mileageMin: filters.mileageMin || null,
        mileageMax: filters.mileageMax || null,
        fuelType: filters.fuelType || null,
        selectedFuels: filters.selectedFuels || [],
        selectedBodyTypes: filters.selectedBodyTypes || [],
        selectedSeats: filters.selectedSeats || [],
        selectedExteriorColors: filters.selectedExteriorColors || [],
        selectedInteriorColors: filters.selectedInteriorColors || [],
        selectedDrivetrains: filters.selectedDrivetrains || [],
        selectedFeatures: filters.selectedFeatures || [],
        emailAlertsEnabled: true,
      };

      const response = await api.post('/saved-searches', searchData);
      if (response.data.success) {
        setShowSaveDialog(false);
        setSearchName('');
        setLastSuggestedName('');
        setEmail('');
        toast.success('Search saved successfully! You\'ll receive email alerts when new cars match your criteria.');
      }
    } catch (error) {
      console.error('Error saving search:', error);
      toast.error(error.response?.data?.message || 'Failed to save search. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  function clearAll() {
    setFilters(createEmptyFilters());
  }

  const saveCurrentPreset = () => {
    const name = `Preset ${new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
    const preset = {
      id: `preset_${Date.now()}`,
      name,
      filters,
      createdAt: new Date().toISOString(),
    };
    setFilterPresets((prev) => {
      const next = [preset, ...prev].slice(0, 12);
      return next;
    });
  };

  const applyPreset = (presetId) => {
    const preset = filterPresets.find((p) => p.id === presetId);
    if (!preset?.filters) return;
    setFilters({ ...createEmptyFilters(), ...preset.filters });
  };

  const deletePreset = (presetId) => {
    setFilterPresets((prev) => prev.filter((p) => p.id !== presetId));
  };

  // Remove individual filter
  function removeFilter(filterType, value) {
    switch (filterType) {
      case 'search':
        updateFilters({ search: '' });
        break;
      case 'vin':
        updateFilters({ vin: '' });
        break;
      case 'priceRange':
        updateFilters({ priceMin: '', priceMax: '' });
        break;
      case 'yearRange':
        updateFilters({ yearMin: '', yearMax: '' });
        break;
      case 'mileage':
        updateFilters({ mileageMin: '', mileageMax: '' });
        break;
      case 'make':
        updateFilters({ selectedMakes: filters.selectedMakes.filter(m => m !== value) });
        break;
      case 'model':
        updateFilters({ selectedModels: filters.selectedModels.filter(m => m !== value) });
        break;
      case 'fuel':
        updateFilters({ selectedFuels: filters.selectedFuels.filter(f => f !== value) });
        break;
      case 'bodyType':
        updateFilters({ selectedBodyTypes: filters.selectedBodyTypes.filter(b => b !== value) });
        break;
      case 'seats':
        updateFilters({ selectedSeats: filters.selectedSeats.filter(s => s !== value) });
        break;
      case 'exteriorColor':
        updateFilters({ selectedExteriorColors: filters.selectedExteriorColors.filter(c => c !== value) });
        break;
      case 'interiorColor':
        updateFilters({ selectedInteriorColors: filters.selectedInteriorColors.filter(c => c !== value) });
        break;
      case 'drivetrain':
        updateFilters({ selectedDrivetrains: filters.selectedDrivetrains.filter(d => d !== value) });
        break;
      case 'feature':
        updateFilters({ selectedFeatures: filters.selectedFeatures.filter(f => f !== value) });
        break;
      default:
        break;
    }
  }

  // Get active filters for display
  const activeFilters = useMemo(() => {
    const tags = [];

    // Search is now auto-converted to specific filters, so we don't need to show it separately
    // But if there's still a search that didn't match any pattern, show it
    if (filters.search) {
      // Format the search nicely (capitalize first letter)
      let formattedSearch = filters.search.trim();
      formattedSearch = formattedSearch.charAt(0).toUpperCase() + formattedSearch.slice(1);
      tags.push({ type: 'search', label: formattedSearch, value: filters.search });
    }
    if (filters.vin) {
      const normalizedVin = normalizeVinQuery(filters.vin);
      tags.push({ type: 'vin', label: `VIN: ${normalizedVin}`, value: normalizedVin });
    }

    if (filters.priceMin || filters.priceMax) {
      const min = filters.priceMin ? `$${Number(filters.priceMin).toLocaleString()}` : 'Any';
      const max = filters.priceMax ? `$${Number(filters.priceMax).toLocaleString()}` : 'Any';
      tags.push({ type: 'priceRange', label: `Price: ${min} - ${max}` });
    }

    if (filters.yearMin || filters.yearMax) {
      const min = filters.yearMin || 'Any';
      const max = filters.yearMax || 'Any';
      tags.push({ type: 'yearRange', label: `Year: ${min} - ${max}` });
    }

    if (filters.mileageMin || filters.mileageMax) {
      const min = filters.mileageMin ? `${Number(filters.mileageMin).toLocaleString()}` : 'Any';
      const max = filters.mileageMax ? `${Number(filters.mileageMax).toLocaleString()}` : 'Any';
      tags.push({ type: 'mileage', label: `Mileage: ${min} - ${max} mi` });
    }

    filters.selectedMakes.forEach(make => {
      tags.push({ type: 'make', label: make, value: make });
    });

    filters.selectedModels.forEach(model => {
      tags.push({ type: 'model', label: model, value: model });
    });

    filters.selectedFuels.forEach(fuel => {
      tags.push({ type: 'fuel', label: fuel.charAt(0).toUpperCase() + fuel.slice(1), value: fuel });
    });

    filters.selectedBodyTypes.forEach(bodyType => {
      tags.push({ type: 'bodyType', label: bodyType.charAt(0).toUpperCase() + bodyType.slice(1), value: bodyType });
    });

    filters.selectedSeats.forEach(seats => {
      tags.push({ type: 'seats', label: `${seats} Seats`, value: seats });
    });

    filters.selectedExteriorColors.forEach(color => {
      tags.push({ type: 'exteriorColor', label: `Exterior: ${color}`, value: color });
    });

    filters.selectedInteriorColors.forEach(color => {
      tags.push({ type: 'interiorColor', label: `Interior: ${color}`, value: color });
    });

    filters.selectedDrivetrains.forEach(drivetrain => {
      tags.push({ type: 'drivetrain', label: drivetrain.toUpperCase(), value: drivetrain });
    });

    filters.selectedFeatures.forEach(feature => {
      tags.push({ type: 'feature', label: feature, value: feature });
    });

    return tags;
  }, [filters]);

  const filtered = useMemo(() => {
    const toNum = (v) => (v === '' || v === undefined || v === null ? undefined : Number(v));
    const priceMin = toNum(filters.priceMin);
    const priceMax = toNum(filters.priceMax);
    const yearMin = toNum(filters.yearMin);
    const yearMax = toNum(filters.yearMax);
    const mileageMin = toNum(filters.mileageMin);
    const mileageMax = toNum(filters.mileageMax);
    const search = (filters.search || '').toLowerCase();
    const vinQuery = normalizeVinQuery(filters.vin);
    const makeQuery = (filters.make || '').toLowerCase();
    const modelQuery = (filters.model || '').toLowerCase();

    let list = cars || [];

    // Backend already returns only customer-visible statuses (available, on_hold_soft, reserved_hard, purchase_in_progress, etc.)
    // So show all cars returned by the API.
    list = list.filter((c) => {
      const statusOk = true;
      if (!statusOk) return false;
      const carPrice = c.price != null && c.price !== '' ? Number(c.price) : null;
      const priceOk =
        (priceMin === undefined || (carPrice != null && !Number.isNaN(carPrice) && carPrice >= priceMin)) &&
        (priceMax === undefined || (carPrice != null && !Number.isNaN(carPrice) && carPrice <= priceMax));
      const yearOk =
        (yearMin === undefined || (c.year != null && c.year >= yearMin)) &&
        (yearMax === undefined || (c.year != null && c.year <= yearMax));
      const mileageOk =
        (mileageMin === undefined || (c.mileage != null && c.mileage >= mileageMin)) &&
        (mileageMax === undefined || (c.mileage != null && c.mileage <= mileageMax));
      const fuelOk = !filters.fuelType || (c.fuelType || '').toLowerCase() === filters.fuelType;
      const selectedFuelsOk =
        !filters.selectedFuels || filters.selectedFuels.length === 0 ||
        (c.fuelType && filters.selectedFuels.includes(String(c.fuelType).toLowerCase()));
      const selectedMakesOk =
        !filters.selectedMakes || filters.selectedMakes.length === 0 ||
        (c.make && filters.selectedMakes.map((m) => String(m).toLowerCase()).includes(String(c.make).toLowerCase()));
      const makeOk = !makeQuery || (c.make || '').toLowerCase().includes(makeQuery);
      const modelOk = !modelQuery || (c.model || '').toLowerCase().includes(modelQuery);
      const selectedModelsOk =
        !filters.selectedModels || filters.selectedModels.length === 0 ||
        (c.model && filters.selectedModels.map((m) => String(m).toLowerCase()).includes(String(c.model).toLowerCase()));
      const seatsOk =
        !filters.selectedSeats || filters.selectedSeats.length === 0 ||
        (c.seats != null && filters.selectedSeats.includes(String(c.seats)));
      const bodyTypeOk =
        !filters.selectedBodyTypes || filters.selectedBodyTypes.length === 0 ||
        (c.bodyType && filters.selectedBodyTypes.includes(String(c.bodyType).toLowerCase()));
      const exteriorOk =
        !filters.selectedExteriorColors || filters.selectedExteriorColors.length === 0 ||
        (c.exteriorColor && filters.selectedExteriorColors.includes(String(c.exteriorColor).toLowerCase()));
      const interiorOk =
        !filters.selectedInteriorColors || filters.selectedInteriorColors.length === 0 ||
        (c.interiorColor && filters.selectedInteriorColors.includes(String(c.interiorColor).toLowerCase()));
      const drivetrainOk =
        !filters.selectedDrivetrains || filters.selectedDrivetrains.length === 0 ||
        (c.drivetrain && filters.selectedDrivetrains.some((d) => {
          const raw = String(c.drivetrain).toLowerCase();
          if (d === 'awd') return raw.includes('all') && raw.includes('wheel');
          if (d === '4wd') return raw.includes('4') && (raw.includes('wheel') || raw.includes('x4'));
          if (d === 'fwd') return raw.includes('front');
          if (d === 'rwd') return raw.includes('rear');
          return raw.includes(d);
        }));
      const featuresOk =
        !filters.selectedFeatures || filters.selectedFeatures.length === 0 ||
        (() => {
          const carFeatures = getCarFeatureList(c.features).map((x) => String(x).toLowerCase());
          return filters.selectedFeatures.every((fk) =>
            carFeatures.includes(String(fk).toLowerCase())
          );
        })();
      const vinValue = normalizeVinQuery(c.vin);
      const vinOk = !vinQuery || (vinValue && (vinValue === vinQuery || vinValue.startsWith(vinQuery)));
      // Enhanced search that checks multiple fields comprehensively
      let searchOk = !search;
      if (search) {
        const searchLower = search.toLowerCase();
        const searchTrimmed = searchLower.trim();

        // Check if search is only digits (single or multiple) - if so, only match specific patterns
        const isOnlyDigits = /^\d+$/.test(searchTrimmed);
        const searchNum = searchTrimmed.replace(/[^\d]/g, '');
        const hasDigits = searchNum.length > 0;

        // Special handling for "X seats" or "X seat" pattern (case-insensitive, with or without spaces)
        const seatsMatch = searchTrimmed.match(/(\d+)\s*seats?/i);
        if (seatsMatch && c.seats != null) {
          const seatNumber = parseInt(seatsMatch[1], 10);
          searchOk = String(c.seats) === String(seatNumber);
        }

        // If search is only digits and not a seats pattern, don't search numeric fields
        // This prevents "4" from matching all years/prices/mileage containing "4"
        if (!searchOk) {
          // For make, model, and bodyType: match from start of word (e.g., "for" matches "Ford" but not "comfort")
          // This prevents partial words from matching unrelated results
          const makeMatch = c.make && c.make.toLowerCase().startsWith(searchTrimmed);
          const modelMatch = c.model && c.model.toLowerCase().startsWith(searchTrimmed);
          const bodyTypeMatch = c.bodyType && c.bodyType.toLowerCase().startsWith(searchTrimmed);

          // For other important fields: also use word-start matching for better precision
          const trimMatch = c.trim && c.trim.toLowerCase().startsWith(searchTrimmed);
          const fuelTypeMatch = c.fuelType && c.fuelType.toLowerCase().startsWith(searchTrimmed);
          const transmissionMatch = c.transmission && c.transmission.toLowerCase().startsWith(searchTrimmed);
          const drivetrainMatch = c.drivetrain && c.drivetrain.toLowerCase().startsWith(searchTrimmed);

          // For less critical fields: use word boundary matching (whole words only)
          // This prevents "for" from matching "comfort", "performance", etc.
          const wordBoundaryRegex = new RegExp(`\\b${searchTrimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
          const engineTypeMatch = c.engineType && c.engineType && wordBoundaryRegex.test(c.engineType);
          const exteriorColorMatch = c.exteriorColor && wordBoundaryRegex.test(c.exteriorColor);
          const interiorColorMatch = c.interiorColor && wordBoundaryRegex.test(c.interiorColor);
          const conditionMatch = c.condition && wordBoundaryRegex.test(c.condition);

          // For exact match fields (stockNumber, VIN): use includes but require minimum length
          const stockNumberMatch = c.stockNumber && searchTrimmed.length >= 3 && c.stockNumber.toLowerCase().includes(searchTrimmed);
          const vinMatch = c.vin && c.vin && searchTrimmed.length >= 3 && c.vin.toLowerCase().includes(searchTrimmed);

          searchOk =
            makeMatch ||
            modelMatch ||
            bodyTypeMatch ||
            trimMatch ||
            fuelTypeMatch ||
            transmissionMatch ||
            drivetrainMatch ||
            engineTypeMatch ||
            stockNumberMatch ||
            vinMatch ||
            exteriorColorMatch ||
            interiorColorMatch ||
            conditionMatch;
        }

        // Check numeric fields ONLY if search is NOT just digits (to avoid "4" matching everything)
        // OR if search has at least 2 digits (like "2023", "30000", etc.)
        if (!searchOk && hasDigits && (!isOnlyDigits || searchNum.length >= 2)) {
          searchOk =
            (c.year != null && String(c.year).includes(searchNum)) ||
            (c.price != null && String(c.price).includes(searchNum)) ||
            (c.mileage != null && String(c.mileage).includes(searchNum));
        }

        // Check features array (comfort, technology, safety)
        if (!searchOk && c.features) {
          const allFeatures = getCarFeatureList(c.features);
          searchOk = allFeatures.some((feat) =>
            String(feat).toLowerCase().includes(searchTrimmed)
          );
        }

        // Check if search matches full car name (e.g., "2024 Toyota Camry")
        if (!searchOk && c.year && c.make && c.model) {
          const fullName = `${c.year} ${c.make} ${c.model}`.toLowerCase();
          if (fullName.includes(searchTrimmed)) {
            searchOk = true;
          }
        }
      }
      return priceOk && yearOk && mileageOk && fuelOk && selectedFuelsOk && selectedMakesOk && makeOk && modelOk && selectedModelsOk && seatsOk && bodyTypeOk && exteriorOk && interiorOk && drivetrainOk && featuresOk && vinOk && searchOk;
    });

    // For "best" sort: surface aged inventory (60/90/120+ days) in top 2-3 pages
    // Score = newness + age boost — aged cars rank higher to sell faster
    const getBestScore = (car) => {
      const createdAt = car?.createdAt;
      const days = !createdAt ? 0 : Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
      const newness = 1 / (1 + days);
      const ageBoost = getInventoryAgeBoost(car);
      return newness + ageBoost;
    };
    const compare = {
      best: (a, b) => getBestScore(b) - getBestScore(a),
      priceAsc: (a, b) => (a.price ?? Infinity) - (b.price ?? Infinity),
      priceDesc: (a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity),
      priceDrop: (a, b) => {
        const dropA = (a.isPriceDrop && a.originalPrice) ? (parseFloat(a.originalPrice) - parseFloat(a.price)) : 0;
        const dropB = (b.isPriceDrop && b.originalPrice) ? (parseFloat(b.originalPrice) - parseFloat(b.price)) : 0;
        return dropB - dropA; // Biggest savings first
      },
      yearDesc: (a, b) => (b.year ?? -Infinity) - (a.year ?? -Infinity),
      mileageAsc: (a, b) => (a.mileage ?? Infinity) - (b.mileage ?? Infinity),
    }[filters.sortBy || 'best'];

    const sortedList = [...list].sort(compare);

    // Apply advanced ML-based recommendations (only if no active filters)
    // Uses statistical analysis, temporal decay, Gaussian similarity, and frequency weighting
    const hasActiveFilters = filters.selectedMakes.length > 0 || filters.selectedModels.length > 0 ||
      filters.make || filters.model || filters.priceMin || filters.priceMax ||
      filters.yearMin || filters.yearMax || filters.selectedFuels.length > 0 ||
      filters.selectedBodyTypes.length > 0;

    // Get wishlist cars (full car objects from wishlist IDs)
    const wishlistCars = wishlistIds
      .map(id => cars.find(car => String(car.id) === String(id)))
      .filter(Boolean); // Remove undefined entries

    // Combine recently viewed and wishlist cars for recommendations
    const hasUserData = recentlyViewedCars.length > 0 || wishlistCars.length > 0;

    if (!hasActiveFilters && hasUserData) {
      // Use advanced ML recommendation engine with:
      // - Statistical range matching (std dev, IQR, CV)
      // - Temporal decay (recent views weighted higher)
      // - Frequency-weighted preferences
      // - Gaussian similarity for numerical features
      // - Cosine similarity for categorical features
      // - Wishlist cars (explicitly saved by user)
      return applyRecommendations(sortedList, recentlyViewedCars, { wishlistCars });
    }

    return sortedList;
  }, [cars, filters, recentlyViewedCars, wishlistIds]);

  const optionLists = useMemo(() => {
    const makesSet = new Set();
    const modelsSet = new Set();
    const yearsSet = new Set();
    const fuelsSet = new Set();
    const fuelCounts = new Map();
    const makeCounts = new Map();
    const modelsByMake = new Map(); // make -> Map(model -> count)
    const seatsCounts = new Map();
    const exteriorCounts = new Map();
    const interiorCounts = new Map();
    const drivetrainCounts = new Map();
    const bodyTypeCounts = new Map();
    const featureCounts = new Map();

    // Only count cars with 'available' status for filter counts
    (cars || []).filter(c => c.status === 'available').forEach((c) => {
      if (c.make) makesSet.add(String(c.make));
      if (c.make) makeCounts.set(String(c.make), (makeCounts.get(String(c.make)) || 0) + 1);
      if (!filters.make && c.model) modelsSet.add(String(c.model));
      if (filters.make && c.make && String(c.make).toLowerCase() === String(filters.make).toLowerCase() && c.model) {
        modelsSet.add(String(c.model));
      }
      if (c.make && c.model) {
        const mk = String(c.make);
        const md = String(c.model);
        if (!modelsByMake.has(mk)) modelsByMake.set(mk, new Map());
        const mm = modelsByMake.get(mk);
        mm.set(md, (mm.get(md) || 0) + 1);
      }
      if (c.year != null) yearsSet.add(Number(c.year));
      if (c.fuelType) {
        const key = String(c.fuelType).toLowerCase();
        fuelsSet.add(key);
        fuelCounts.set(key, (fuelCounts.get(key) || 0) + 1);
      }
      if (c.seats != null) {
        const skey = String(c.seats);
        seatsCounts.set(skey, (seatsCounts.get(skey) || 0) + 1);
      }
      if (c.exteriorColor) {
        const key = String(c.exteriorColor).toLowerCase();
        exteriorCounts.set(key, (exteriorCounts.get(key) || 0) + 1);
      }
      if (c.interiorColor) {
        const key = String(c.interiorColor).toLowerCase();
        interiorCounts.set(key, (interiorCounts.get(key) || 0) + 1);
      }
      if (c.drivetrain) {
        const raw = String(c.drivetrain).toLowerCase();
        let key = raw;
        if (raw.includes('all') && raw.includes('wheel')) key = 'awd';
        else if (raw.includes('4') && (raw.includes('wheel') || raw.includes('x4'))) key = '4wd';
        else if (raw.includes('front')) key = 'fwd';
        else if (raw.includes('rear')) key = 'rwd';
        drivetrainCounts.set(key, (drivetrainCounts.get(key) || 0) + 1);
      }
      if (c.bodyType) {
        const key = String(c.bodyType).toLowerCase();
        bodyTypeCounts.set(key, (bodyTypeCounts.get(key) || 0) + 1);
      }
      getCarFeatureList(c.features).forEach((feat) => {
        const k = String(feat).toLowerCase();
        featureCounts.set(k, (featureCounts.get(k) || 0) + 1);
      });
    });

    const makes = Array.from(makesSet).sort((a, b) => a.localeCompare(b));
    const models = Array.from(modelsSet).sort((a, b) => a.localeCompare(b));
    const years = Array.from(yearsSet).sort((a, b) => b - a); // desc
    const fuelTypes = Array.from(fuelsSet).sort((a, b) => a.localeCompare(b));
    const fuelsWithCounts = fuelTypes.map((f) => ({ key: f, count: fuelCounts.get(f) || 0 }));

    // Create makes list from catalog with actual inventory first (count > 0), then others with count 0
    const allMakesWithCounts = MAKES_MODELS.map((name) => ({
      name,
      count: makeCounts.get(name) || 0
    }));
    const makesWithInventory = allMakesWithCounts.filter(m => m.count > 0).sort((a, b) => a.name.localeCompare(b.name));
    const makesWithoutInventory = allMakesWithCounts.filter(m => m.count === 0).sort((a, b) => a.name.localeCompare(b.name));
    const makesWithCounts = [...makesWithInventory, ...makesWithoutInventory];

    // Build models list aggregated from selected makes (or all if none selected)
    const selectedMakes = (filters.selectedMakes && filters.selectedMakes.length > 0) ? filters.selectedMakes : makes;
    const aggregateModelCounts = new Map();
    selectedMakes.forEach((mk) => {
      const map = modelsByMake.get(mk);
      if (!map) return;
      for (const [modelName, count] of map.entries()) {
        aggregateModelCounts.set(modelName, (aggregateModelCounts.get(modelName) || 0) + count);
      }
    });
    const modelsForSelectedWithCounts = Array.from(aggregateModelCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const modelsByMakeWithCounts = {};
    for (const [mk, map] of modelsByMake.entries()) {
      modelsByMakeWithCounts[mk] = Array.from(map.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    const seatsWithCounts = Array.from(seatsCounts.entries()).map(([num, count]) => ({ num, count }));
    const exteriorWithCounts = Array.from(exteriorCounts.entries()).map(([key, count]) => ({ key, count }));
    const interiorWithCounts = Array.from(interiorCounts.entries()).map(([key, count]) => ({ key, count }));
    const drivetrainWithCounts = Array.from(drivetrainCounts.entries()).map(([key, count]) => ({ key, count }));

    // Create body types list from catalog with actual inventory first (count > 0), then others with count 0
    const allBodyTypesWithCounts = BODY_TYPES.map((bt) => {
      // Sum up counts for the main key and all aliases
      const aliasSum = (bt.aliases || []).reduce((acc, alias) => acc + (bodyTypeCounts.get(alias) || 0), 0);
      const totalCount = (bodyTypeCounts.get(bt.key) || 0) + aliasSum;
      return {
        key: bt.key,
        label: bt.label,
        count: totalCount,
      };
    });
    const bodyTypesWithInventory = allBodyTypesWithCounts.filter(bt => bt.count > 0).sort((a, b) => a.label.localeCompare(b.label));
    const bodyTypesWithoutInventory = allBodyTypesWithCounts.filter(bt => bt.count === 0).sort((a, b) => a.label.localeCompare(b.label));
    const bodyTypeWithCounts = [...bodyTypesWithInventory, ...bodyTypesWithoutInventory];

    const featuresWithCounts = Array.from(featureCounts.entries()).map(([key, count]) => ({ key, count }));
    return { makes, makesWithCounts, models, years, fuelTypes, fuelsWithCounts, modelsForSelectedWithCounts, modelsByMakeWithCounts, seatsWithCounts, exteriorWithCounts, interiorWithCounts, drivetrainWithCounts, bodyTypeWithCounts, featuresWithCounts };
  }, [cars, filters.make, filters.selectedMakes]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageCars = filtered.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Save comparedCars to localStorage whenever it changes
  useEffect(() => {
    try {
      if (comparedCars.length > 0) {
        localStorage.setItem('comparedCars', JSON.stringify(comparedCars));
      } else {
        localStorage.removeItem('comparedCars');
      }
    } catch (error) {
      console.error('Error saving compared cars to localStorage:', error);
    }
  }, [comparedCars]);

  // Persist filter presets locally (advanced saved filters)
  useEffect(() => {
    try {
      localStorage.setItem('inventoryFilterPresets', JSON.stringify(filterPresets));
    } catch (error) {
      console.error('Error saving filter presets to localStorage:', error);
    }
  }, [filterPresets]);

  // Validate comparedCars against current cars data when cars load
  useEffect(() => {
    if (cars && cars.length > 0 && comparedCars.length > 0) {
      const validCarIds = comparedCars.filter(carId =>
        cars.some(car => car.id === carId)
      );
      if (validCarIds.length !== comparedCars.length) {
        setComparedCars(validCarIds);
      }
    } else if (cars && cars.length > 0 && comparedCars.length === 0) {
      // If cars loaded and no compared cars, ensure localStorage is clear
      localStorage.removeItem('comparedCars');
    }
  }, [cars, comparedCars]); // Re-run when `cars` data or `comparedCars` changes

  const handleCompareChange = (carId, isAdding) => {
    setComparedCars(prev => {
      if (isAdding) {
        if (prev.length >= maxCompare) {
          toast.error(`You can only compare up to ${maxCompare} cars at once.`);
          return prev;
        }
        return [...prev, carId];
      } else {
        return prev.filter(id => id !== carId);
      }
    });
  };

  const handleRemoveFromComparison = (carId) => {
    setComparedCars(prev => prev.filter(id => id !== carId));
  };

  const getComparedCarsData = () => {
    return comparedCars.map(id => cars.find(c => c.id === id)).filter(Boolean);
  };

  const handleShowComparison = () => {
    if (comparedCars.length < 2) {
      toast.error('Please select at least 2 cars to compare.');
      return;
    }
    setShowComparison(true);
  };

  const marketInsightsById = useMemo(() => {
    const map = new Map();
    if (!cars?.length) return map;
    cars.forEach((c) => {
      const insight = computeMarketComparison(c, cars);
      if (insight) map.set(c.id, insight);
    });
    return map;
  }, [cars]);

  // Restore comparison from shareable URL (?compare=id1,id2)
  useEffect(() => {
    const compareParam = searchParams.get('compare');
    if (!compareParam || !cars?.length) return;
    const ids = compareParam.split(',').map((s) => s.trim()).filter(Boolean).slice(0, maxCompare);
    const valid = ids.filter((id) => cars.some((c) => String(c.id) === id));
    if (valid.length >= 2) {
      setComparedCars(valid);
      setShowComparison(true);
    }
  }, [searchParams, cars, maxCompare]);

  // Keep compare ids in URL for sharing (after inventory loads)
  useEffect(() => {
    if (!cars?.length) return;
    const next = new URLSearchParams(searchParams);
    if (comparedCars.length >= 2) {
      next.set('compare', comparedCars.join(','));
    } else if (!searchParams.get('compare')) {
      next.delete('compare');
    } else if (comparedCars.length === 0) {
      return;
    } else {
      next.delete('compare');
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [comparedCars, cars?.length, searchParams, setSearchParams]);

  return (
    <section
      className={`inventory-section${showMobileFilters ? ' filter-modal-open' : ''}`}
    >
      <Navbar />

      {/* Mobile Filter & Compare Bar */}
      <div className={`mobile-filter-bar ${showMobileFilters ? 'hidden' : ''}`}>
        <button
          className="mobile-filter-btn"
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          aria-label={t('inventory.filters')}
        >
          <Filter size={18} strokeWidth={2.5} />
          <span>{t('inventory.filter')}</span>
        </button>
        <button
          className={`mobile-compare-btn ${comparedCars.length >= 2 ? 'active' : ''}`}
          onClick={handleShowComparison}
          disabled={comparedCars.length < 2}
          aria-label={t('inventory.compareCars')}
        >
          <span>{t('carCard.compare')} {comparedCars.length > 0 ? `(${comparedCars.length})` : ''}</span>
        </button>
      </div>

      <div className="inventory-layout">
        <FiltersSidebar
          filters={filters}
          onChange={updateFilters}
          onClear={clearAll}
          options={optionLists}
          className={showMobileFilters ? 'mobile-filters-open' : 'mobile-filters-closed'}
          onClose={() => setShowMobileFilters(false)}
        />
        <div className={`inventory-results ${comparedCars.length > 0 ? 'has-compare-slider' : ''}`}>
          <div className="inventory-meta inventory-meta--with-cta">
            <div className="inventory-meta-primary">
              <span className="inventory-results-count">{filtered.length} {t('inventory.results')}</span>
            </div>
            <Link to="/get-financed" className="inventory-primary-cta btn btn-primary">
              {t('home.getPreApproved')}
            </Link>
          </div>

          {/* Active Filter Tags */}
          {activeFilters.length > 0 && (
            <div className="active-filters">
              <div className="active-filters-header">
                <span className="active-filters-label">{t('inventory.activeFiltersLabel')}</span>
                <div className="active-filters-actions">
                  <button
                    className="save-search-btn"
                    onClick={() => {
                      if (!isAuthenticated) {
                        toast.error(t('inventory.pleaseLogInToSave'));
                        return;
                      }
                      setShowSaveDialog(true);
                    }}
                    title={t('inventory.saveSearchTitle')}
                  >
                    <Save size={16} />
                    <span>{t('inventory.saveSearch')}</span>
                  </button>
                  <button
                    className={`share-link-btn ${linkCopied ? 'copied' : ''}`}
                    onClick={handleShareLink}
                    title={linkCopied ? t('inventory.linkCopied') : t('inventory.shareFilteredView')}
                  >
                    {linkCopied ? (
                      <>
                        <CheckCircle2 size={16} />
                        <span>{t('inventory.copied')}</span>
                      </>
                    ) : (
                      <>
                        <Share2 size={16} />
                        <span>{t('inventory.share')}</span>
                      </>
                    )}
                  </button>
                  <button
                    className="clear-all-btn"
                    onClick={clearAll}
                  >
                    {t('inventory.clearAll')}
                  </button>
                  <button
                    className="save-search-btn"
                    onClick={saveCurrentPreset}
                    title="Save current filters as a preset"
                  >
                    <Save size={16} />
                    <span>Save Preset</span>
                  </button>
                  {filterPresets.length > 0 && (
                    <>
                      <select
                        className="clear-all-btn"
                        onChange={(e) => {
                          if (!e.target.value) return;
                          applyPreset(e.target.value);
                          e.target.value = '';
                        }}
                        defaultValue=""
                        title="Apply saved filter preset"
                      >
                        <option value="" disabled>Apply Preset</option>
                        {filterPresets.map((preset) => (
                          <option key={preset.id} value={preset.id}>
                            {preset.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="clear-all-btn"
                        onClick={() => {
                          const latest = filterPresets[0];
                          if (!latest) return;
                          confirmDialog({
                            title: 'Delete preset',
                            description: `Delete preset "${latest.name}"?`,
                            confirmLabel: 'Delete',
                            destructive: true,
                          }).then((ok) => {
                            if (ok) deletePreset(latest.id);
                          });
                        }}
                        title="Delete latest saved preset"
                      >
                        Delete Preset
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="filter-tags">
                {activeFilters.map((filter, index) => (
                  <button
                    key={`${filter.type}-${filter.value || ''}-${index}`}
                    className="filter-tag"
                    onClick={() => removeFilter(filter.type, filter.value)}
                    title={`Remove ${filter.label}`}
                  >
                    <span className="filter-tag-label">{filter.label}</span>
                    <X size={14} className="filter-tag-icon" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Save Search Dialog */}
          {showSaveDialog && (
            <div className="save-search-dialog-overlay" onClick={() => {
              setShowSaveDialog(false);
              setSearchName('');
              setLastSuggestedName('');
              setEmail('');
            }}>
              <div className="save-search-dialog-modal" onClick={(e) => e.stopPropagation()}>
                <div className="save-search-dialog-header">
                  <h3>Email search updates</h3>
                  <button
                    className="save-search-dialog-close"
                    onClick={() => {
                      setShowSaveDialog(false);
                      setSearchName('');
                      setLastSuggestedName('');
                      setEmail('');
                    }}
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="save-search-dialog-content">
                  <p className="save-search-dialog-subtitle">
                    <Info size={14} />
                    Get email updates when matches are added.
                  </p>

                  <div className="save-search-dialog-field">
                    <label htmlFor="search-name">
                      Name Search
                    </label>
                    <input
                      id="search-name"
                      type="text"
                      placeholder="Enter a name for this search..."
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && searchName.trim() && !saving) {
                          handleSaveSearch();
                        }
                      }}
                      onFocus={(e) => {
                        // Select all text when focused for easy editing
                        e.target.select();
                      }}
                      autoFocus
                    />
                  </div>

                  {!isAuthenticated && (
                    <div className="save-search-dialog-field">
                      <label htmlFor="search-email">
                        Enter email <span className="required-asterisk">*</span>
                      </label>
                      <input
                        id="search-email"
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  )}
                  {isAuthenticated && user?.email && (
                    <div className="save-search-dialog-field">
                      <label htmlFor="search-email">
                        Email notifications will be sent to:
                      </label>
                      <input
                        id="search-email"
                        type="email"
                        value={user.email}
                        disabled
                        className="disabled-input"
                      />
                    </div>
                  )}

                  <div className="save-search-email-options">
                    <label className="save-search-radio-label">
                      <input
                        type="radio"
                        name="emailNotificationType"
                        value="instant"
                        checked={emailNotificationType === 'instant'}
                        onChange={(e) => setEmailNotificationType(e.target.value)}
                      />
                      <span>Send matches instantly</span>
                    </label>
                    <label className="save-search-radio-label">
                      <input
                        type="radio"
                        name="emailNotificationType"
                        value="daily"
                        checked={emailNotificationType === 'daily'}
                        onChange={(e) => setEmailNotificationType(e.target.value)}
                      />
                      <span>Send daily summary</span>
                    </label>
                  </div>

                  <div className="save-search-legal-links">
                    See our <Link to="/privacy-policy">Privacy Policy</Link> and <Link to="/terms-of-service">Terms of Use</Link>.
                  </div>

                  {!isAuthenticated && (
                    <div className="save-search-account-prompt">
                      <p>
                        <Link to="/auth?mode=register" onClick={() => setShowSaveDialog(false)}>
                          Create an account
                        </Link> to save this search to your dashboard.
                      </p>
                    </div>
                  )}

                  <div className="save-search-dialog-actions">
                    <button
                      onClick={handleSaveSearch}
                      disabled={saving || !searchName.trim() || (!isAuthenticated && !email.trim())}
                      className="save-search-submit-btn"
                    >
                      {saving ? 'Saving...' : 'Get email updates'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Compare Bar - Bottom Slider */}
          {comparedCars.length > 0 && (
            <div className="compare-bar-slider">
              <div className="compare-bar-slider-content">
                <div className="compare-bar-slider-left">
                  <span className="compare-bar-slider-text">
                    {t('inventory.comparingItems', { current: comparedCars.length, max: maxCompare })}
                  </span>
                  <div className="compare-bar-slider-items">
                    {comparedCars.map((carId) => {
                      const car = cars.find(c => c.id === carId);
                      if (!car) return null;
                      return (
                        <div key={carId} className="compare-bar-item">
                          <img
                            src={car.images && car.images.length > 0 ? car.images[0] : VEHICLE_PLACEHOLDER_URL}
                            alt={`${car.year} ${car.make} ${car.model}`}
                            className="compare-bar-item-image"
                          />
                          <div className="compare-bar-item-info">
                            <span className="compare-bar-item-name">
                              {car.year} {car.make} {car.model}
                            </span>
                          </div>
                          <button
                            className="compare-bar-item-remove"
                            onClick={() => handleRemoveFromComparison(carId)}
                            aria-label={t('inventory.removeFromComparison')}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      );
                    })}
                    {comparedCars.length < maxCompare && (
                      <>
                        {[...Array(maxCompare - comparedCars.length)].map((_, index) => (
                          <div key={`empty-${index}`} className="compare-bar-item-empty">
                            <span className="compare-bar-item-empty-icon">+</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                <button
                  className="compare-bar-slider-button"
                  onClick={handleShowComparison}
                  disabled={comparedCars.length < 2}
                >
                  {t('carCard.compare')}
                </button>
              </div>
            </div>
          )}

          <div className="inventory-grid">
            {/* Show skeleton loaders while cars are loading */}
            {(!cars || cars.length === 0) && filtered.length === 0 ? (
              <>
                {[...Array(12)].map((_, index) => (
                  <div key={`skeleton-${index}`} className="car-card-skeleton">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-title-line1"></div>
                      <div className="skeleton-title-line2"></div>
                      <div className="skeleton-meta">
                        <div className="skeleton-badge"></div>
                        <div className="skeleton-text-small"></div>
                      </div>
                      <div className="skeleton-price"></div>
                      <div className="skeleton-payment"></div>
                      <div className="skeleton-shipping">
                        <div className="skeleton-text-medium"></div>
                        <div className="skeleton-text-small"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                {currentPageCars.map((car) => (
                  <CarCard
                    key={car.id}
                    car={car}
                    onCompareChange={handleCompareChange}
                    isCompared={comparedCars.includes(car.id)}
                    maxCompareReached={comparedCars.length >= maxCompare}
                    marketInsight={marketInsightsById.get(car.id) || null}
                  />
                ))}
                {filtered.length === 0 && cars && cars.length > 0 && (
                  <div className="empty">
                    <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
                      {t('inventory.noCarsMatch')}
                    </p>
                    <p style={{ fontSize: '14px', color: '#64748b' }}>
                      {t('inventory.tryClearingFilters')}
                    </p>
                    <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                      {t('inventory.totalCarsInDb')} {cars?.length || 0} |
                      {t('inventory.availableCars')} {cars?.filter(c => c.status === 'available').length || 0}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="pagination-numbers">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;

                  // Always show first 3 pages (1, 2, 3)
                  if (pageNumber <= 3) {
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  }

                  // Show ellipsis after page 3 if there are more pages before the last 2
                  if (pageNumber === 4 && totalPages > 5) {
                    return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                  }

                  // Always show last 2 pages (n-1, n)
                  if (pageNumber >= totalPages - 1) {
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  }

                  return null;
                })}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />

      {/* Comparison Modal */}
      {showComparison && (
        <CarComparison
          cars={getComparedCarsData()}
          onClose={() => setShowComparison(false)}
          onRemove={handleRemoveFromComparison}
        />
      )}
    </section>
  );
}

export default Inventory;